import { useState } from "react";
import styled from "styled-components";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AllyService from "../../../services/ally.service";
import { format } from "date-fns";
import { Reservation, TourInfo } from "../../../types/common.types";
import ToursService from "../../../services/tours.service";
import { useGlobal } from "../../../context/global";
import formatNumberToString from "../../../utils/formatNumber";
import removeTimezone from "../../../utils/removeTimezone";
import { toDayString } from "../../../utils/dayString";
import { useAlly } from "../../../context/ally";
import m, { desktop } from "../../../styles/molanturTheme";
import { Button, Icon } from "../ui";


export default function ConfirmBoucher() {
    const { setMessage } = useGlobal()
    const { setIsConfirmBoucherModalOpen, newGroupReservations, summary, 
        setIsTicketPaymentGroupModalOpen, setIsSummaryModalOpen, newGroupReservationData, setNewGroupReservationData, newGroup } = useAlly()
    const [isBoucherDownloaded, setIsBoucherDownloaded] = useState(false)

    async function printBoucher() {
    if (!newGroupReservationData?.tour_id) return;
    
    const allyInfo = await (await AllyService.me()).data;
    const tours: TourInfo[] = [];
    
    for (let i = 0; i < newGroupReservations.length; i++) {
        const reservation = newGroupReservations[i];
        const tourInfo: TourInfo = await (await ToursService.getDetailTourInfo(reservation.tour_id, allyInfo.agency_id)).data;
        tours.push(tourInfo);
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Add fonts - with error handling
    try {
        const fontNormalUrl = '/fonts/Roboto-Regular.ttf';
        const fontMediumUrl = '/fonts/Roboto-Medium.ttf';
        const fontBoldUrl = '/fonts/Roboto-Bold.ttf';
        
        doc.addFont(fontNormalUrl, 'Roboto', 'normal');
        doc.addFont(fontMediumUrl, 'Roboto', 'medium');
        doc.addFont(fontBoldUrl, 'Roboto', 'bold');
        console.log('Fonts added');
    } catch (error) {
        console.warn('Font loading failed, using default fonts:', error);
        // Fallback to default fonts
        doc.setFont('helvetica');
    }

    try {
            const imageUrl = '/assets/logo.png';
            const imagePadding = 2;
            const imageSize = 30; // Square size
            const imageX = 14; // Left margin
            const imageY = 5; // Top margin
            
            doc.addImage(
                imageUrl, 
                'PNG', 
                imageX + imagePadding, 
                imageY + imagePadding, 
                imageSize - (imagePadding * 2), 
                imageSize - (imagePadding * 2),
                undefined,
                // 'FAST' = comprimir el logo (17/10/2026): sin esto jsPDF lo guarda en crudo y el voucher pesa ~4,7 MB.
                'FAST'
            );
        
        } catch (error) {
            console.log('Could not load image:', error);
        }

    let y = 15;
    let currentPage = 1;

    // Function to check if we need a new page
    const checkPageBreak = (requiredSpace = 20) => {
        if (y + requiredSpace > pageHeight - 20) {
            doc.addPage();
            y = 20;
            currentPage++;
            return true;
        }
        return false;
    };

    // Header with company name
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(18);
    doc.text("MOLANTUR", 105, y, { align: "center" });
    y += 8;
    doc.setFontSize(14);
    doc.text("ATACAMA TOURS", 105, y, { align: "center" });
    y += 15;

    // Check page break after header
    checkPageBreak();

    // Reservation details table
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(10);
    
    // Reservation number and date
    autoTable(doc, {
        startY: y,
        body: [
            [
                { 
                    content: `Número de Reserva\n#${newGroupReservationData.group_id}`,
                    styles: { fontStyle: 'bold', halign: 'left' }
                },
                { 
                    content: `Fecha:\n${format(new Date(), 'dd/MM/yyyy')}`,
                    styles: { fontStyle: 'bold', halign: 'left' }
                },
                { 
                    content: `Cantidad de pasajeros\n${newGroup.length}`,
                    styles: { fontStyle: 'bold', halign: 'left' }
                }
            ]
        ],
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 50 },
            2: { cellWidth: 40 }
        },
        margin: { left: 20, right: 20 }
    });
    
    console.log('Reservation details added');
    y = (doc as any).lastAutoTable.finalY + 10;

    // Check page break before passengers table
    checkPageBreak(50);

    // Passengers table
    const passengers = newGroup.map((passenger, index) => {
        return [
            index + 1, 
            passenger.name || '', 
            passenger.rut_passport || '', 
            passenger.age ? `${passenger.age} y/o` : '', 
            passenger.phone || ''
        ];
    });

    console.log(passengers);

    autoTable(doc, {
        startY: y,
        head: [["#", "Nombre", "Documento", "EDAD", "CONTACTO"]],
        body: passengers,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { 
            fillColor: [100, 100, 100], 
            textColor: 255, 
            fontStyle: 'bold',
            halign: "center" 
        },
        bodyStyles: { halign: "center" },
        margin: { left: 20, right: 20 },
        // Enable page breaks for long tables
        didDrawPage: function (data) {
            // Update y position after table is drawn
            y = (data.cursor?.y || y) + 10;
        }
    });
    
    y = (doc as any).lastAutoTable.finalY + 15;

    // Check page break before tour details
    checkPageBreak(100);

    // Tour details header
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(12);
    doc.text("Detalles de la reserva", 20, y);
    y += 10;

    // Prepare tour details data
    const tourDetails = [];
    console.log(newGroupReservationData)
    console.log(newGroupReservations)
    function reduceReservations(reservations: Reservation[]): Reservation[] {
    const grouped: any = {};
    
    // Group by tour_id AND reservation_date
    reservations.forEach(reservation => {
        // Convert price and tickets to numbers for consistent calculation
        const price = Number(reservation.price) || 0;
        const tickets = Number(reservation.tickets) || 0;
        const tourId = reservation.tour_id;
        // El día elegido, no su hora universal (desde Europa, toISOString() daba el día anterior).
        const date = removeTimezone(toDayString(reservation.reservation_date));
        
        // Create a unique key combining tour_id and reservation_date
        const key = `${tourId}_${date}`;
        console.log(key)
        if (!grouped[key]) {
            grouped[key] = {
                tour_id: tourId,
                price: price,
                tickets: tickets,
                reservation_date: date
            };
        } else {
            // Sum the prices and tickets for same tour_id AND same date
            grouped[key].price += price;
            grouped[key].tickets += tickets;
        }
    });
    
    // Convert the grouped object back to array
    return Object.values(grouped);
    }
    const newGroupReservationsReduced: Reservation[] = reduceReservations(newGroupReservations)
    for (let i = 0; i < newGroupReservationsReduced.length; i++) {
        const reservation = newGroupReservationsReduced[i];
        const tourInfo = tours.find(tour => tour.tour_id === reservation.tour_id);
        
        tourDetails.push([
            i + 1,
            tourInfo?.tour_id || `Tour ${reservation.tour_id}`,
            reservation.reservation_date.toString(),
            tourInfo?.include?.join(', ') || 'No definido',
            tourInfo?.recommendations || tourInfo?.recommendations_es ||"Las recomendaciones que estan anotadas en cada tour",
            `$${reservation.price ? formatNumberToString(Number(reservation.price)) : '0'}`,
            reservation.tickets ? `$${formatNumberToString(Number(reservation.tickets))}` : '$0',
            `$${formatNumberToString(Number(reservation.price) + Number(reservation.tickets)) || '0'}`
        ]);
    }

    console.log(tourDetails);

    // Tour details table with adjusted column widths to fit page
    autoTable(doc, {
        startY: y,
        head: [["#", "Tour", "Fecha", "Incluido", "Recomendaciones", "Total Tour", "Total Tickets", "Total a pagar"]],
        body: tourDetails,
        theme: "grid",
        styles: { fontSize: 6, cellPadding: 2 }, // Smaller font for better fit
        headStyles: { 
            fillColor: [100, 100, 100], 
            textColor: 255, 
            fontStyle: 'bold',
            halign: "center",
            minCellHeight: 8,
            fontSize: 6
        },
        bodyStyles: { 
            halign: "center",
            minCellHeight: 8,
            fontSize: 6
        },
        // Adjusted column widths to fit page width
        columnStyles: {
            0: { cellWidth: 8 },
            1: { cellWidth: 30 }, // Reduced
            2: { cellWidth: 18 }, // Reduced
            3: { cellWidth: 30 }, // Reduced
            4: { cellWidth: 35 }, // Reduced
            5: { cellWidth: 25 }, // Reduced
            6: { cellWidth: 18 }, // Reduced
            7: { cellWidth: 18 }  // Reduced
        },
        margin: { left: 10, right: 10 }, // Reduced margins
        // Enable page breaks
        didDrawPage: function (data) {
            y = (data.cursor?.y || y) + 10;
        }
    });
    
    y = (doc as any).lastAutoTable.finalY + 10;

    // Check page break before totals
    checkPageBreak(30);

    // Calculate totals
    const totalTour = newGroupReservations.reduce((sum, res) => sum + (Number(res.price) || 0), 0);
    const totalTickets = newGroupReservations.reduce((sum, res) => sum + (Number(res.tickets) || 0), 0);
    const totalPagar = totalTour + totalTickets;
    console.log(totalPagar)

    // FIXED: Totals table with proper width calculation
    const availableWidth = pageWidth - 30; // 15px margin on each side
    autoTable(doc, {
        startY: y,
        body: [
            [
                { 
                    content: "TOTALES",
                    styles: { fontStyle: 'bold', halign: 'right' }
                },
                { 
                    content: `$${formatNumberToString(totalTour)}`,
                    styles: { fontStyle: 'bold', halign: 'center' }
                },
                { 
                    content: `$${formatNumberToString(totalTickets)}`,
                    styles: { fontStyle: 'bold', halign: 'center' }
                },
                { 
                    content: `$${formatNumberToString(totalPagar)}`,
                    styles: { fontStyle: 'bold', halign: 'center' }
                }
            ]
        ],
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 5 },
        columnStyles: {
            0: { cellWidth: availableWidth * 0.45 }, // label (17/10/2026: más ancho para los montos, que se partían en dos líneas)
            1: { cellWidth: availableWidth * 0.18 }, // 13% for each value
            2: { cellWidth: availableWidth * 0.18 },
            3: { cellWidth: availableWidth * 0.18 }
        },
        margin: { left: 15, right: 15 }
    });
    
    y = (doc as any).lastAutoTable.finalY + 20;

    // Check page break before terms and conditions
    checkPageBreak(100);

    // Terms and conditions
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(12);
    doc.text("Condiciones generales y políticas de cancelación", 20, y);
    y += 15;

    // Function to add text with automatic page breaks
    const addTextWithPageBreaks = (text: string, fontSize = 9, isBold = false) => {
        if (isBold) {
            doc.setFont('Roboto', 'bold');
        } else {
            doc.setFont('Roboto', 'normal');
        }
        doc.setFontSize(fontSize);
        
        const splitText = doc.splitTextToSize(text, pageWidth - 40);
        
        for (let i = 0; i < splitText.length; i++) {
            // Check if we need a new page for each line
            if (y > pageHeight - 15) {
                doc.addPage();
                y = 20;
                currentPage++;
            }
            doc.text(splitText[i], 20, y);
            y += 5; // Line height
        }
        y += 5; // Extra space after paragraph
    };

    // Cancelation policy
    addTextWithPageBreaks("Cancelaciones y devoluciones:", 10, true);
    addTextWithPageBreaks("Con un periodo de 48 horas se puede cancelar con una devolución del 80% con menos de ese tiempo no se efectuará ninguna devolución. Sobre los cambios de itinerario pedimos las mismas 48 horas para realizar el cambio sin costo, sin embargo nos reservamos el derecho de aceptar o rechazar el cambio dependiendo nuestra disponibilidad.");

    // Responsibility exemption
    addTextWithPageBreaks("Exención de responsabilidad:", 10, true);
    addTextWithPageBreaks("Molantur se reserva el derecho de modificar itinerarios debido a condiciones climáticas o situaciones ajenas a nuestro control. No nos hacemos responsables por pérdidas personales o retrasos ocasionados por estas situaciones.");

    // Health and safety
    addTextWithPageBreaks("Salud y seguridad:", 10, true);
    addTextWithPageBreaks("Algunos tours se realizan por encima de los 4.000 metros sobre el nivel del mar. Se recomienda programar un período suficiente de aclimatación y realizar evaluación médica previa en caso de condiciones preexistentes ya que no se realizará devolución por temas médicos ni de falta de aclimatación a la altura.");

    // Restrictions
    addTextWithPageBreaks("Restricciones:", 10, true);
    addTextWithPageBreaks("Pasajeros bajo influencia de alcohol o drogas no podrán participar.");

    // Park closures
    addTextWithPageBreaks("Cierre imprevisto de parques o atracciones:", 10, true);
    addTextWithPageBreaks("Ante cierre imprevisto se cobrará hasta el 50 % del valor del tour para cubrir costos operativos.");

    // International tours documentation
    addTextWithPageBreaks("Documentación para tours internacionales (Uyuni):", 10, true);
    addTextWithPageBreaks("Es responsabilidad del pasajero verificar la documentación migratoria actualizada (pasaporte válido, permisos notariales para menores, visas, etc.). La falta de documentación adecuada implica la pérdida total del monto pagado.");

    // Insurance
    addTextWithPageBreaks("Seguro:", 10, true);
    addTextWithPageBreaks("Este servicio no incluye seguro de vida ni gastos médicos.");

    // Emergency contact
    addTextWithPageBreaks("Contacto de Emergencia:", 10, true);
    
    // Check page break before contact info
    checkPageBreak(20);
    
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(10);
    doc.text("WhatsApp / Atención inmediata: +56 9 7577 4192", 25, y);
    y += 6;
    doc.text("Oficina Molantur: Pasaje Vilama 425-A, San Pedro de Atacama, Chile", 25, y);
    y += 15;

    // Final acceptance text
    checkPageBreak(15);
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(9);
    const acceptanceText = "Al hacer uso de este voucher, el pasajero declara haber leído, comprendido y aceptado todas las condiciones aquí descritas.";
    addTextWithPageBreaks(acceptanceText);

    // Save PDF
    doc.save(`voucher_reserva_${newGroupReservationData.group_id || 'reserva'}.pdf`);
}
    async function handleDownloadBoucher() {
        try {
            await printBoucher()
            setIsBoucherDownloaded(true)
            setMessage({
                type: 'success',
                content: 'Boucher descargado correctamente'
            })
        } catch (error) {
            setMessage({
                type: 'error',
                content: 'Ocurrió un error, por favor intenta de nuevo'
            })
            console.log(error);
        } finally {
            setTimeout(() => {
                setMessage(undefined)
            }, 5000)
        }
    }

    function proceed() {
        window.location.reload()
    }
    return (
        <Overlay>
            <Dialog role="dialog" aria-labelledby="voucher-title">
                <span className="ok"><Icon name="check" size={26} stroke={2.4} /></span>
                <h1 id="voucher-title">Reserva enviada a Molantur</h1>
                <p>Descarga el voucher para entregárselo a tus huéspedes y luego confirma.</p>
                <div className="actions">
                    <Button full size="lg" icon="download" onClick={handleDownloadBoucher}>Descargar voucher</Button>
                    {
                        isBoucherDownloaded &&
                        <Button kind="dark" full onClick={proceed}>Confirmar</Button>
                    }
                    <Button kind="ghost" full icon="plus"
                    onClick={() => {
                        // La siguiente reservación parte sin tour ni "entrada incluida" elegidos: antes
                        // quedaba seleccionado el tour anterior y era fácil reenviarlo sin querer (y la
                        // base lo rechaza como duplicado). Se conservan grupo y fecha — el caso típico
                        // es otro tour el mismo día (ej. Valle de la Luna + astronómico).
                        setNewGroupReservationData(prev => ({ ...prev, tour_id: '', ticket_included: undefined }))
                        setIsConfirmBoucherModalOpen(false)
                        setIsTicketPaymentGroupModalOpen(false)
                    }}
                    >Agregar otro tour a estos huéspedes</Button>
                    {
                        summary && summary.length > 0 &&
                        <Button kind="quiet" full icon="list" onClick={() => setIsSummaryModalOpen(true)}>Ver resumen</Button>
                    }
                </div>
            </Dialog>
        </Overlay>
    )
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(43,38,32,0.5);
    ${desktop} {
        align-items: center;
        padding: 24px;
    }
`

const Dialog = styled.div`
    width: 100%;
    max-width: 440px;
    padding: 26px 22px calc(22px + env(safe-area-inset-bottom));
    text-align: center;
    background: ${m.color.bg};
    border-radius: 28px 28px 0 0;
    box-shadow: ${m.shadow.modal};
    font-family: ${m.font.text};
    color: ${m.color.ink};
    ${desktop} { border-radius: ${m.radius.lg}; }
    .ok {
        width: 54px;
        height: 54px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: ${m.color.successSoft};
        color: ${m.color.success};
    }
    h1 {
        margin: 14px 0 6px;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 24px;
    }
    p {
        margin: 0 auto 20px;
        max-width: 320px;
        font-size: 14px;
        line-height: 1.45;
        color: ${m.color.inkSoft};
    }
    .actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
`
